import { useEffect, useMemo, useRef, useState, type CSSProperties, type UIEvent } from 'react';
import { createPortal } from 'react-dom';

import type { FormResponses as Data, ResponseRow } from '../data/responses.js';
import type { Form } from '../fixtures/domain.js';
import { Checkbox } from '../components/Checkbox.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon, type IconName } from '../components/Icon.js';
import { formatClock, formatDate } from '../util/format.js';
import { moveKey } from '../util/reorder.js';
import { useDismiss } from '../util/useDismiss.js';
import { useDragReorder } from '../util/useDragReorder.js';
import { useColumnDrag, type ColumnDrag } from './useColumnDrag.js';
import {
  DEFAULT_SORT,
  columnsFor,
  shownByDefault,
  isActive,
  isDefaultSort,
  sortRows,
  valueCounts,
  visibleRows,
  type ColumnFilter,
  type Filters,
  type ResponseColumn,
  type Sort,
  type SortKind,
  idOf,
} from './responseColumns.js';
import { ResponsePanel } from './ResponsePanel.js';
import styles from './FormResponses.module.css';

const PAGE_SIZES = [10, 25, 50];
/** An amount box takes up to RM 9,999,999. */
const AMOUNT_DIGITS = 7;
/** Past this many values a filter menu gets a search box. */
const SEARCH_FROM = 6;
const MIN_WIDTH = 96;
const MAX_WIDTH = 640;
const KEY_STEP = 16;
const FILTER_MENU_WIDTH = 208;
const VIEW_MENU_WIDTH = 264;

/** The arrow points the way values grow, and the glyphs read top to bottom. */
const SORT_ICONS: Record<SortKind, { ascending: IconName; descending: IconName }> = {
  number: { ascending: 'arrowDown01', descending: 'arrowUp10' },
  text: { ascending: 'arrowDownAZ', descending: 'arrowUpZA' },
};

type Popover = { kind: 'view'; anchor: DOMRect } | { kind: 'filter'; key: string; anchor: DOMRect };

/** Every answer to one form, one row per jobseeker, sieved column by column. */
export function FormResponses({
  form,
  data,
  reviewId,
  onReview,
  reviewHost,
  onOpenChat,
  onChatExists,
}: {
  form: Form;
  data: Data;
  reviewId: string | null;
  onReview: (id: string | null) => void;
  reviewHost: HTMLElement | null;
  onOpenChat: (cid: string) => void;
  onChatExists: (cid: string) => Promise<boolean>;
}) {
  const columns = useMemo(() => columnsFor(form), [form]);
  const rows = data.rows;
  /** The shown columns, in the order the table draws them; everything else is hidden. */
  const [arranged, setArranged] = useState<string[]>(() =>
    columns.filter(shownByDefault).map((column) => column.key),
  );
  const [widths, setWidths] = useState<Readonly<Record<string, number>>>({});
  const [filters, setFilters] = useState<Filters>(new Map());
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
  const [query, setQuery] = useState('');
  const [popover, setPopover] = useState<Popover | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]!);
  const [page, setPage] = useState(0);

  const shownColumns = useMemo(() => {
    const byKey = new Map(columns.map((column) => [column.key, column]));
    return [
      ...columns.filter((column) => column.pinned),
      ...arranged.flatMap((key) => byKey.get(key) ?? []),
    ];
  }, [columns, arranged]);
  const view = useMemo(
    () => ({ columns, filters, query, searchIn: shownColumns }),
    [columns, filters, query, shownColumns],
  );
  const matching = useMemo(
    () => sortRows(visibleRows(rows, view), columns, sort),
    [rows, view, columns, sort],
  );
  const pages = Math.max(1, Math.ceil(matching.length / pageSize));
  const onPage = Math.min(page, pages - 1);
  const shown = matching.slice(onPage * pageSize, (onPage + 1) * pageSize);

  const moveColumn = (from: string, to: string) =>
    setArranged((current) => moveKey(current, from, to));
  const columnDrag = useColumnDrag(arranged, moveColumn);
  const nudge = (key: string, step: 1 | -1) => {
    const target = arranged[arranged.indexOf(key) + step];
    if (target) moveColumn(key, target);
  };

  const reviewed = reviewId ? rows.find((row) => idOf(row) === reviewId) : undefined;
  // The open response's page comes to it, from a link or a step past the page's edge.
  const reviewedAt = reviewId ? matching.findIndex((row) => idOf(row) === reviewId) : -1;
  const reviewedPage = reviewedAt === -1 ? null : Math.floor(reviewedAt / pageSize);
  useEffect(() => {
    if (reviewedPage !== null) setPage(reviewedPage);
  }, [reviewId, reviewedPage]);
  // A link can name an answer this list never holds (a colleague's send, one
  // past the newest thousand, a failed read): the column is let go, not left blank.
  const unresolved = reviewId !== null && !data.loading && !reviewed;
  useEffect(() => {
    if (unresolved) onReview(null);
  }, [unresolved, onReview]);

  /** Sorting counts as applying something, so the column's funnel says so. */
  const columnActive = (column: ResponseColumn) =>
    isActive(filters.get(column.key)) || (sort.key === column.key && !isDefaultSort(sort));
  const anyActive = query.trim() !== '' || columns.some(columnActive);

  // Anything that narrows or reorders the set starts it again from its first page.
  const search = (next: string) => {
    setQuery(next);
    setPage(0);
  };
  const sortBy = (next: Sort) => {
    setSort(next);
    setPage(0);
  };
  const setFilter = (key: string, filter: ColumnFilter | null) => {
    setFilters((current) => {
      const next = new Map(current);
      if (filter) next.set(key, filter);
      else next.delete(key);
      return next;
    });
    setPage(0);
  };
  const clearColumn = (key: string) => {
    setFilter(key, null);
    if (sort.key === key) sortBy(DEFAULT_SORT);
  };
  const clearAll = () => {
    setFilters(new Map());
    sortBy(DEFAULT_SORT);
    search('');
  };
  /** Shown at the end of the order; put away with its filter and sort, or rows
   *  vanish for no visible reason. */
  const toggleColumn = (key: string) => {
    if (arranged.includes(key)) {
      clearColumn(key);
      setArranged((current) => current.filter((entry) => entry !== key));
    } else {
      setArranged((current) => [...current, key]);
    }
  };
  const resize = (key: string, width: number) =>
    setWidths((current) => {
      const next = Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width)));
      return current[key] === next ? current : { ...current, [key]: next };
    });
  const widthOf = (column: ResponseColumn) => widths[column.key] ?? column.width;

  if (data.loading) return null;
  if (data.failed) {
    return (
      <EmptyState
        icon={<Icon name="listChecks" size={32} />}
        title="Couldn't load the answers"
        action={{ label: 'Try again', onClick: data.retry }}
      />
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Icon name="listChecks" size={32} />}
        title="No answers yet"
        description="Answers to this form land here from every chat you send it in."
      />
    );
  }

  const filterColumn =
    popover?.kind === 'filter' ? columns.find((column) => column.key === popover.key) : undefined;
  const tableWidth = shownColumns.reduce((sum, column) => sum + widthOf(column), 0);

  return (
    <div className={styles.responses}>
      <div className={styles.toolbar}>
        <label className={styles.search}>
          <Icon name="search" size={15} />
          <input
            value={query}
            placeholder="Search"
            aria-label="Search responses"
            onChange={(event) => search(event.target.value)}
          />
        </label>
        <span className={styles.tools}>
          {anyActive && (
            <button type="button" className={styles.clear} onClick={clearAll}>
              Clear filters
            </button>
          )}
          <button
            type="button"
            className={popover?.kind === 'view' ? styles.viewOn : styles.view}
            aria-expanded={popover?.kind === 'view'}
            onClick={(event) =>
              setPopover(
                popover?.kind === 'view'
                  ? null
                  : { kind: 'view', anchor: event.currentTarget.getBoundingClientRect() },
              )
            }
          >
            <Icon name="layoutList" size={15} />
            View
          </button>
        </span>
      </div>

      <div className={styles.frame}>
        <div
          className={scrolled ? styles.scrollerScrolled : styles.scroller}
          onScroll={(event: UIEvent<HTMLDivElement>) =>
            setScrolled(event.currentTarget.scrollLeft > 0)
          }
        >
          <table className={styles.table} style={{ width: `max(${tableWidth}px, 100%)` }}>
            <thead>
              <tr>
                {shownColumns.map((column) => (
                  <HeaderCell
                    key={column.key}
                    column={column}
                    width={widthOf(column)}
                    active={columnActive(column)}
                    open={popover?.kind === 'filter' && popover.key === column.key}
                    onOpen={(anchor) =>
                      setPopover(anchor ? { kind: 'filter', key: column.key, anchor } : null)
                    }
                    onResize={(width) => resize(column.key, width)}
                    drag={column.pinned ? null : columnDrag}
                    onNudge={(step) => nudge(column.key, step)}
                  />
                ))}
                <th className={styles.fillerHead} aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <ResponseRowView
                  key={idOf(row)}
                  row={row}
                  columns={shownColumns}
                  open={reviewId === idOf(row)}
                  onOpen={() => onReview(idOf(row))}
                  dragState={columnDrag.stateOf}
                />
              ))}
            </tbody>
          </table>
          {shown.length === 0 && <p className={styles.none}>No responses match.</p>}
        </div>
        {data.truncated && <p className={styles.truncated}>Showing the latest 1,000 answers</p>}
        <Pager
          page={onPage}
          pages={pages}
          pageSize={pageSize}
          total={matching.length}
          onPage={setPage}
          onPageSize={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      </div>

      {columnDrag.carried && (
        <div ref={columnDrag.ghost} className={styles.dragGhost}>
          {columns.find((column) => column.key === columnDrag.carried)?.label}
        </div>
      )}
      {filterColumn && popover?.kind === 'filter' && (
        <FilterMenu
          column={filterColumn}
          anchor={popover.anchor}
          counts={
            filterColumn.filter === 'values'
              ? valueCounts(filterColumn, visibleRows(rows, { ...view, except: filterColumn.key }))
              : []
          }
          filter={filters.get(filterColumn.key)}
          sort={sort}
          active={columnActive(filterColumn)}
          onFilter={(filter) => setFilter(filterColumn.key, filter)}
          onSort={(descending) =>
            sortBy(
              sort.key === filterColumn.key && sort.descending === descending
                ? DEFAULT_SORT
                : { key: filterColumn.key, descending },
            )
          }
          onClear={() => clearColumn(filterColumn.key)}
          onClose={() => setPopover(null)}
        />
      )}
      {popover?.kind === 'view' && (
        <ViewMenu
          columns={columns}
          arranged={arranged}
          anchor={popover.anchor}
          onToggle={toggleColumn}
          onMove={moveColumn}
          onClose={() => setPopover(null)}
        />
      )}
      {reviewed &&
        reviewHost &&
        createPortal(
          <ResponsePanel
            row={reviewed}
            rows={matching}
            onSelect={onReview}
            onOpenChat={onOpenChat}
            onChatExists={onChatExists}
          />,
          reviewHost,
        )}
    </div>
  );
}

function HeaderCell({
  column,
  width,
  active,
  open,
  onOpen,
  onResize,
  drag,
  onNudge,
}: {
  column: ResponseColumn;
  width: number;
  active: boolean;
  open: boolean;
  onOpen: (anchor: DOMRect | null) => void;
  onResize: (width: number) => void;
  /** Absent on the name, which stays first. */
  drag: ColumnDrag | null;
  onNudge: (step: 1 | -1) => void;
}) {
  const action = column.filter === 'none' ? 'Sort' : column.sort ? 'Sort or filter' : 'Filter';
  const state = drag?.stateOf(column.key);
  return (
    <th
      scope="col"
      className={[column.pinned ? styles.personHead : styles.head, state && HEAD_DRAG[state]]
        .filter(Boolean)
        .join(' ')}
      style={{ width }}
      data-column-key={column.key}
    >
      <span className={column.align === 'right' ? styles.headInnerEnd : styles.headInner}>
        {drag ? (
          <span
            className={styles.headTextMovable}
            title={column.label}
            tabIndex={0}
            aria-label={`${column.label}. Drag, or use the left and right arrow keys, to move this column.`}
            onPointerDown={drag.start(column.key)}
            onKeyDown={(event) => {
              if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
              event.preventDefault();
              onNudge(event.key === 'ArrowRight' ? 1 : -1);
            }}
          >
            {column.label}
          </span>
        ) : (
          <span className={styles.headText} title={column.label}>
            {column.label}
          </span>
        )}
        <button
          type="button"
          className={active ? styles.funnelOn : styles.funnel}
          aria-label={`${action} ${column.label}`}
          aria-expanded={open}
          onClick={(event) => onOpen(open ? null : event.currentTarget.getBoundingClientRect())}
        >
          <Icon name="filter" size={14} filled={active} />
        </button>
      </span>
      <ResizeHandle label={column.label} width={width} initial={column.width} onResize={onResize} />
    </th>
  );
}

/** A header being carried, and the bar where it would land, on the side it lands. */
const HEAD_DRAG = {
  dragging: styles.headDragging,
  overBefore: styles.headOverBefore,
  overAfter: styles.headOverAfter,
} as const;

/** The details panel's resizer, on a column edge: drag, arrow keys, double-click to reset. */
function ResizeHandle({
  label,
  width,
  initial,
  onResize,
}: {
  label: string;
  width: number;
  initial: number;
  onResize: (width: number) => void;
}) {
  const drag = useRef<{ x: number; width: number } | null>(null);
  const frame = useRef(0);
  const end = () => {
    drag.current = null;
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label}`}
      aria-valuemin={MIN_WIDTH}
      aria-valuemax={MAX_WIDTH}
      aria-valuenow={width}
      tabIndex={0}
      className={styles.resizer}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { x: event.clientX, width };
      }}
      onPointerMove={(event) => {
        if (!drag.current) return;
        const next = drag.current.width + event.clientX - drag.current.x;
        cancelAnimationFrame(frame.current);
        frame.current = requestAnimationFrame(() => onResize(next));
      }}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => onResize(initial)}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        onResize(width + (event.key === 'ArrowRight' ? KEY_STEP : -KEY_STEP));
      }}
    />
  );
}

function ResponseRowView({
  row,
  columns,
  open,
  onOpen,
  dragState,
}: {
  row: ResponseRow;
  columns: ResponseColumn[];
  open: boolean;
  onOpen: () => void;
  /** A column being carried dims all the way down, and its landing line runs the full height. */
  dragState: ColumnDrag['stateOf'];
}) {
  const name = row.person.name ?? 'Jobseeker';
  return (
    <tr className={open ? styles.rowOpen : styles.row} onClick={onOpen}>
      {columns.map((column) =>
        column.pinned ? (
          <th key={column.key} scope="row" className={styles.person}>
            <button
              type="button"
              className={styles.name}
              aria-pressed={open}
              onClick={(event) => {
                event.stopPropagation();
                onOpen();
              }}
            >
              {name}
            </button>
          </th>
        ) : (
          <td
            key={column.key}
            className={[
              column.align === 'right' ? styles.cellEnd : styles.cell,
              dragState(column.key) && HEAD_DRAG[dragState(column.key)!],
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <Cell column={column} row={row} />
          </td>
        ),
      )}
      <td className={styles.filler} />
    </tr>
  );
}

/** One pill and a count of the rest: a row of pills cut off mid-word is worse than either. */
function Cell({ column, row }: { column: ResponseColumn; row: ResponseRow }) {
  if (column.when) {
    const at = column.when(row);
    return <>{`${formatDate(at)}, ${formatClock(at)}`}</>;
  }
  const values = column.values(row);
  if (values.length === 0) {
    return <span className={styles.blank}>{column.blankCell ?? column.blank}</span>;
  }
  if (column.pills) {
    const [first, ...rest] = values;
    return (
      <span className={styles.pills} title={values.join(', ')}>
        <span className={styles.pill}>{first}</span>
        {rest.length > 0 && <span className={styles.pillMore}>+{rest.length}</span>}
      </span>
    );
  }
  return (
    <span
      className={column.filter === 'text' ? styles.clamp : styles.value}
      title={values.join(', ')}
    >
      {values.join(', ')}
    </span>
  );
}

function Pager({
  page,
  pages,
  pageSize,
  total,
  onPage,
  onPageSize,
}: {
  page: number;
  pages: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}) {
  const from = page * pageSize;
  const showing = Math.min(pageSize, total - from);
  return (
    <div className={styles.pager}>
      <span>{total === 0 ? 'Showing 0' : `Showing ${from + 1}–${from + showing} of ${total}`}</span>
      <label className={styles.pageSize}>
        Rows per page
        <span className={styles.select}>
          <select value={pageSize} onChange={(event) => onPageSize(Number(event.target.value))}>
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <Icon name="chevronDown" size={14} />
        </span>
      </label>
      <span className={styles.pageNav}>
        <PageButton
          icon="chevronsLeft"
          label="First page"
          disabled={page === 0}
          onPress={() => onPage(0)}
        />
        <PageButton
          icon="chevronLeft"
          label="Previous page"
          disabled={page === 0}
          onPress={() => onPage(page - 1)}
        />
        <span className={styles.pageOf}>
          Page {page + 1} of {pages}
        </span>
        <PageButton
          icon="chevronRight"
          label="Next page"
          disabled={page >= pages - 1}
          onPress={() => onPage(page + 1)}
        />
        <PageButton
          icon="chevronsRight"
          label="Last page"
          disabled={page >= pages - 1}
          onPress={() => onPage(pages - 1)}
        />
      </span>
    </div>
  );
}

function PageButton({
  icon,
  label,
  disabled,
  onPress,
}: {
  icon: IconName;
  label: string;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      className={styles.pageButton}
      aria-label={label}
      disabled={disabled}
      onClick={onPress}
    >
      <Icon name={icon} size={16} />
    </button>
  );
}

/** Sort first, then the values: every one ticked to begin with, unticking hides its rows. */
function FilterMenu({
  column,
  anchor,
  counts,
  filter,
  sort,
  active,
  onFilter,
  onSort,
  onClear,
  onClose,
}: {
  column: ResponseColumn;
  anchor: DOMRect;
  counts: [string, number][];
  filter?: ColumnFilter;
  sort: Sort;
  active: boolean;
  onFilter: (filter: ColumnFilter | null) => void;
  onSort: (descending: boolean) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(true, onClose, ref);
  const [find, setFind] = useState('');

  const hidden = filter && 'hidden' in filter ? filter.hidden : new Set<string>();
  const contains = filter && 'contains' in filter ? filter.contains : '';
  const range = filter && 'range' in filter ? filter.range : {};
  const between = filter && 'between' in filter ? filter.between : {};
  const setBetween = (end: 'min' | 'max', raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, AMOUNT_DIGITS);
    const next = { ...between, [end]: digits === '' ? undefined : Number(digits) };
    onFilter(next.min !== undefined || next.max !== undefined ? { between: next } : null);
  };
  const setRange = (next: { from?: string; to?: string }) =>
    onFilter(next.from || next.to ? { range: next } : null);
  const toggle = (value: string) => {
    const next = new Set(hidden);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onFilter(next.size > 0 ? { hidden: next } : null);
  };
  const listed = counts.filter(([value]) =>
    value.toLowerCase().includes(find.trim().toLowerCase()),
  );
  const icons = column.sort && SORT_ICONS[column.sort.kind];
  const here = sort.key === column.key;

  return (
    <div
      ref={ref}
      className={styles.menu}
      style={placeBelow(anchor, FILTER_MENU_WIDTH, 'start')}
      role="dialog"
      aria-label={column.label}
    >
      {icons && (
        <div className={styles.menuSort}>
          <SortOption
            icon={icons.ascending}
            label="Ascending"
            on={here && !sort.descending}
            onPress={() => onSort(false)}
          />
          <SortOption
            icon={icons.descending}
            label="Descending"
            on={here && sort.descending}
            onPress={() => onSort(true)}
          />
        </div>
      )}
      {column.filter === 'date' && (
        <div className={styles.menuRange}>
          <label className={styles.menuDate}>
            From
            <input
              type="date"
              value={range.from ?? ''}
              max={range.to}
              onChange={(event) => setRange({ ...range, from: event.target.value || undefined })}
            />
          </label>
          <label className={styles.menuDate}>
            To
            <input
              type="date"
              value={range.to ?? ''}
              min={range.from}
              onChange={(event) => setRange({ ...range, to: event.target.value || undefined })}
            />
          </label>
        </div>
      )}
      {column.filter === 'number' && (
        <div className={styles.menuRange}>
          {(['min', 'max'] as const).map((end) => (
            <label key={end} className={styles.menuDate}>
              {end === 'min' ? 'Minimum' : 'Maximum'}
              <span className={styles.amount}>
                <span className={styles.amountUnit}>RM</span>
                <input
                  inputMode="numeric"
                  value={between[end]?.toLocaleString('en-MY') ?? ''}
                  onChange={(event) => setBetween(end, event.target.value)}
                />
              </span>
            </label>
          ))}
          {between.min !== undefined && between.max !== undefined && between.min > between.max && (
            <p className={styles.menuWarn}>
              The minimum is above the maximum, so nothing can match.
            </p>
          )}
        </div>
      )}
      {column.filter === 'text' && (
        <label className={styles.menuSearch}>
          <Icon name="search" size={14} />
          <input
            value={contains}
            placeholder="Contains"
            aria-label={`Answers to ${column.label} containing`}
            autoFocus
            onChange={(event) =>
              onFilter(event.target.value ? { contains: event.target.value } : null)
            }
          />
        </label>
      )}
      {column.filter === 'values' && (
        <>
          {counts.length > SEARCH_FROM && (
            <label className={styles.menuSearch}>
              <Icon name="search" size={14} />
              <input
                value={find}
                placeholder="Search"
                aria-label={`Search ${column.label}`}
                autoFocus
                onChange={(event) => setFind(event.target.value)}
              />
            </label>
          )}
          <ul className={styles.menuValues}>
            {listed.map(([value, count]) => (
              <li key={value}>
                <label className={styles.menuValue}>
                  <Checkbox
                    checked={!hidden.has(value)}
                    onChange={() => toggle(value)}
                    label={value}
                  />
                  <span className={value === column.blank ? styles.menuBlank : styles.menuLabel}>
                    {value}
                  </span>
                  <span className={styles.menuCount}>{count}</span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
      {active && (
        <button type="button" className={styles.menuClear} onClick={onClear}>
          Clear
        </button>
      )}
    </div>
  );
}

function SortOption({
  icon,
  label,
  on,
  onPress,
}: {
  icon: IconName;
  label: string;
  on: boolean;
  onPress: () => void;
}) {
  return (
    <button type="button" className={styles.sortOption} aria-pressed={on} onClick={onPress}>
      <Icon name={icon} size={15} />
      <span className={styles.menuLabel}>{label}</span>
      {on && (
        <span className={styles.sortCheck}>
          <Icon name="check" size={15} />
        </span>
      )}
    </button>
  );
}

const HIDDEN_GROUPS: [ResponseColumn['group'], string][] = [
  ['details', 'Jobseeker profile'],
  ['form', 'Form questions'],
  ['others', 'Others'],
];

/** Shown columns in table order, draggable; hidden ones below, grouped. A row
 *  changes section when pressed, so it says "Hide" or "Show". */
function ViewMenu({
  columns,
  arranged,
  anchor,
  onToggle,
  onMove,
  onClose,
}: {
  columns: ResponseColumn[];
  arranged: string[];
  anchor: DOMRect;
  onToggle: (key: string) => void;
  onMove: (from: string, to: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(true, onClose, ref);
  const drag = useDragReorder(arranged, onMove, { dragImage: (grip) => grip.closest('li') });
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const hiddenIn = (group: ResponseColumn['group']) =>
    columns.filter(
      (column) => column.group === group && !column.pinned && !arranged.includes(column.key),
    );
  const groups = HIDDEN_GROUPS.map(([group, title]) => [title, hiddenIn(group)] as const).filter(
    ([, list]) => list.length > 0,
  );

  return (
    <div
      ref={ref}
      className={styles.menu}
      style={placeBelow(anchor, VIEW_MENU_WIDTH, 'end')}
      role="dialog"
      aria-label="Columns"
    >
      <div className={styles.viewBody}>
        {arranged.length > 0 && (
          <section className={styles.viewGroup}>
            <h3 className={styles.viewTitle}>Shown</h3>
            <ul className={styles.menuList} {...drag.listProps()}>
              {arranged.map((key, index) => {
                const column = byKey.get(key);
                if (!column) return null;
                const state = drag.rowState(key, index);
                return (
                  <li
                    key={key}
                    className={[styles.viewRow, state && VIEW_DRAG[state]]
                      .filter(Boolean)
                      .join(' ')}
                    {...drag.targetProps(key)}
                  >
                    <button
                      type="button"
                      className={styles.grip}
                      aria-label={`Move ${column.label}. Use the up and down arrow keys.`}
                      {...drag.sourceProps(key)}
                      onKeyDown={drag.gripKeyDown(index)}
                    >
                      <Icon name="grip" size={15} />
                    </button>
                    <span className={styles.viewLabel} title={column.label}>
                      {column.label}
                    </span>
                    <button
                      type="button"
                      className={styles.verb}
                      aria-label={`Hide ${column.label}`}
                      onClick={() => onToggle(key)}
                    >
                      Hide
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {groups.map(([title, list]) => (
          <section key={title} className={styles.viewGroup}>
            <h3 className={styles.viewTitle}>{title}</h3>
            <ul className={styles.menuList}>
              {list.map((column) => (
                <li key={column.key}>
                  <div className={styles.addRow}>
                    <span className={styles.viewLabel} title={column.label}>
                      {column.label}
                    </span>
                    <button
                      type="button"
                      className={styles.verb}
                      aria-label={`Show ${column.label}`}
                      onClick={() => onToggle(column.key)}
                    >
                      Show
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

const VIEW_DRAG = {
  dragging: styles.viewRowDragging,
  overBefore: styles.viewRowOverBefore,
  overAfter: styles.viewRowOverAfter,
} as const;

/** Fixed to the viewport under its trigger, so the table's scroll box cannot clip it. */
function placeBelow(anchor: DOMRect, width: number, align: 'start' | 'end'): CSSProperties {
  const wanted = align === 'start' ? anchor.left : anchor.right - width;
  return {
    top: anchor.bottom + 4,
    left: Math.max(8, Math.min(wanted, window.innerWidth - width - 8)),
    width,
  };
}
